import { FC, useState } from 'react';
import { Avatar, Button, Col, Input, Row } from 'antd';
import { TbArrowRight, TbInfoCircle, TbSourceCode } from 'react-icons/tb';

import { getEmbedUrlGuidance, validateReportComponentEmbedUrl } from '@/hooks/reportComponents/embedValidation';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import { PlatformForm as Form } from '@/shared/components/forms';
import { OurDocsLink } from '@/shared/components/tags';
import { SmallTooltip } from '@/shared/components/tooltips';
import { useReportComponentStore } from '@/stores';
import { imgLogoStyling } from '@/theme';
import { ReportComponent, ReportComponentType } from '@/types';
import { centerComponentInMiddleofView } from '@/utilities';

interface EmptyEmbedProps {
    component: ReportComponent;
    transformScale?: string;
    onDone?: () => void;
    disabled?: boolean;
}

// ----- Working Embed Format Examples

// src='https://www.youtube.com/embed/VIk1l8WjtxI?si=bLOr1EY-YItFO3YQ'
// src='https://www.loom.com/embed/d2e8bf69dbac49168ac2a12bfb1a34b5'
// src='https://player.vimeo.com/video/436840030'
// src='https://calendar.google.com/calendar/embed?src=tehfr1vdqnprlo2pj4vt7vj9keatucq0%40import.calendar.google.com&ctz=America%2FChicago'
// src='https://outlook.live.com/owa/calendar/00000000-0000-0000-0000-000000000000/b9ed3b8a-519d-4c34-a9ce-f7f7c5461257/cid-658705BC3B5A6A42/index.html'
// src='https://docs.google.com/document/d/1ctEQ7Bc76J5cWqQCnNszfdQ9cDTNT6ELjcR9Bk74o1A/edit?usp=sharing'
// src='https://williamleiby.notion.site/ebd/2cc7b183533580b2a4c4fda7a6977d90?theme=dark'
// src='https://gist.github.com/0xjac/85097472043b697ab57ba1b1c7530274.pibb'
// src='https://app.hex.tech/019b2d42-9922-7009-a97a-754e69f18d21/app/031uJanmUZZBrqGxgG1HGw/latest?embedded=true'
// src='https://miro.com/app/live-embed/uXjVLRQC5EE=/?embedMode=view_only_without_ui&moveToViewport=-5727%2C-2554%2C6746%2C3814&embedId=806984319111'
// src='https://lucid.app/documents/embedded/44b6680d-2224-4124-b89c-1a11fbe1b84e'
// src='https://visualize.graphy.app/view/e24985be-9e40-43c6-9b45-9d1b526d10de'
// src='https://playground.powerbi.com/sampleReportEmbed'
// src='https://lookerstudio.google.com/embed/reporting/0B5FF6JBKbNJxOWItcWo2SVVVeGc/page/DjD'
// src='https://public.tableau.com/views/Satisfied-with-Surveys_10_0/CorrelationDashboard?:showVizHome=no'
// src='https://analytics.zoho.com/open-view/1933239000004418103/abef96e2000398cf33637c428c90c07c'

const EmptyEmbed: FC<EmptyEmbedProps> = ({ component, transformScale = '0.75', onDone, disabled }): JSX.Element => {
    const [loading, setLoading] = useState(false);

    const { embedComponentTypeToIconMap } = useMaps();

    const { token } = useAntDesignTheme();

    // Lucid.co has an annoying embed - where the uuid/unique id is not the same as as the share ID
    // Need to figure out Tableau embed issue - had to do with a query param

    const { updateDraftProperties } = useReportComponentStore();

    const [embedInput, setEmbedInput] = useState({
        componentId: component.id,
        value: component?.embedURL ?? '',
    });
    const input = embedInput.componentId === component.id ? embedInput.value : (component?.embedURL ?? '');
    const setInput = (value: string) => setEmbedInput({ componentId: component.id, value });
    const guidance = getEmbedUrlGuidance(component.type);
    const validation = validateReportComponentEmbedUrl(component.type, input);

    const handleSaveEmbedURL = async () => {
        if (disabled) {
            return;
        }

        if (!validation.isValid) {
            return;
        }

        setLoading(true);

        updateDraftProperties(component.id, {
            embedURL: validation.normalizedUrl,
        });

        onDone?.();
        setLoading(false);
    };

    return (
        <Form
            layout='vertical'
            style={{
                ...centerComponentInMiddleofView,
                height: '100%',
                width: '100%',
                margin: '0 auto',
            }}
        >
            <Form.Item
                label={
                    <>
                        {component?.type}
                        <SmallTooltip
                            title={
                                <>
                                    Not sure where to find the embed link? Learn more in <OurDocsLink /> for
                                    instructions on each Embed Component.
                                </>
                            }
                            padding='0.5rem 0'
                            placement='topLeft'
                        >
                            <TbInfoCircle style={{ verticalAlign: '-0.075rem', marginLeft: '0.25rem' }} />
                        </SmallTooltip>
                    </>
                }
                style={{ marginBottom: 0 }}
            >
                <Row wrap={false} align={'middle'} gutter={[8, 0]}>
                    <Col flex='none'>
                        <Input
                            prefix={
                                <Avatar
                                    size={20}
                                    icon={
                                        component?.type !== ReportComponentType.EMBED ? (
                                            <img
                                                src={
                                                    embedComponentTypeToIconMap[component?.type as ReportComponentType]
                                                }
                                                alt={component?.type}
                                                style={{ ...imgLogoStyling, transform: `scale(${transformScale})` }}
                                            />
                                        ) : (
                                            <TbSourceCode
                                                size='0.875rem'
                                                style={{
                                                    transform: 'rotate(-180deg)',
                                                    color: token.colorTextTertiary,
                                                }}
                                            />
                                        )
                                    }
                                    shape='square'
                                    style={{ marginRight: '0.125rem', border: `1px solid ${token.colorBorder}` }}
                                />
                            }
                            placeholder={guidance.example}
                            status={input && !validation.isValid ? 'error' : undefined}
                            value={input}
                            disabled={disabled}
                            onChange={(e) => setInput(e.target.value)}
                            style={{ paddingLeft: '0.375rem', paddingRight: '0.375rem', minWidth: 16 * 16 }}
                        />
                    </Col>
                    <Col flex='none'>
                        <Button
                            disabled={disabled || !validation.isValid}
                            loading={loading}
                            onClick={() => {
                                handleSaveEmbedURL();
                            }}
                            type='primary'
                        >
                            Embed Link
                            <TbArrowRight />
                        </Button>
                    </Col>
                </Row>
            </Form.Item>
            <div style={{ marginTop: '0.5rem', maxWidth: 16 * 24, color: token.colorTextTertiary }}>
                <small>
                    <TbInfoCircle style={{ verticalAlign: '-0.1rem', marginRight: '0.25rem' }} />
                    {input && !validation.isValid ? validation.message : guidance.hint}
                </small>
                <div>
                    <small style={{ color: token.colorTextQuaternary }}>Example: {guidance.example}</small>
                </div>
            </div>
        </Form>
    );
};

export default EmptyEmbed;
